import React, { useEffect } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiCalendar,
  FiHeart,
  FiShield,
  FiActivity,
  FiArrowRight,
  FiDroplet,
  FiMoon,
  FiSun,
  FiClock,
} from "react-icons/fi";
import {
  FaBaby,
  FaLeaf,
  FaHandsHelping,
  FaRegCalendarCheck,
  FaClinicMedical,
} from "react-icons/fa";
import FAQSection from "../components/FAQSection";
import QuotesSlider from "./QouteSlider";
import pregImg from "../assets/images/preg.png";
import safeImg from "../assets/images/safe1.jpg";
import Exercises from "../assets/images/Exercises.webp";
import CounterSection from "../components/CounterSection";
import { Helmet } from "react-helmet-async";
import ContactSection from "../components/ContactSection";
import ArticlePopup from "../components/ArticlePopup";
import FlowerRain from "../components/FlowerRain";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [pathname]);

  return null;
};

const Home = () => {
  // Feature images
  const featureImages = {
    safeDays: safeImg,
    hormonalExercise: Exercises,
    pregnancyTools: pregImg,
  };

  // CTA Button Component with Light Feminine Theme
  const CTAButton = ({ to, children, color }) => {
    const colorMap = {
      pink: 'bg-pink-50 text-pink-500 border-pink-200 hover:bg-pink-100 hover:border-pink-300',
      purple: 'bg-purple-50 text-purple-500 border-purple-200 hover:bg-purple-100 hover:border-purple-300',
      red: 'bg-red-50 text-red-500 border-red-200 hover:bg-red-100 hover:border-red-300',
      yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100 hover:border-yellow-300',
      blue: 'bg-blue-50 text-blue-500 border-blue-200 hover:bg-blue-100 hover:border-blue-300',
      green: 'bg-green-50 text-green-500 border-green-200 hover:bg-green-100 hover:border-green-300',
    };

    const classes = colorMap[color] || colorMap.pink;
    const arrowColor = color === 'yellow' ? 'text-yellow-700' : `text-${color}-500`;

    return (
      <Link
        to={to}
        className={`inline-flex items-center px-4 py-2 rounded-full font-medium border-2 ${classes} transition-all duration-300 group hover:shadow-md`}
      >
        {children}
        <FiArrowRight className={`ml-2 transition-transform group-hover:translate-x-1 ${arrowColor}`} />
      </Link>
    );
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 relative">
      <FlowerRain />
      <ScrollToTop />
      <Helmet>
        {/* Google Analytics */}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
        ></script>
        <script>
          {`
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-YR8YMW9ERY', {
        page_path: window.location.pathname,
      });
    `}
        </script>
        <title>FlowHearts - Period Calculator & Women's Health Tools</title>
        <meta
          name="description"
          content="Track your menstrual cycle, calculate ovulation days, pregnancy probability, and safe days. Get personalized health tips for periods and fertility."
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://flowhearts.com/" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://flowhearts.com/" />
        <meta
          property="og:title"
          content="FlowHearts - Period Calculator & Women's Health Tools"
        />
        <meta
          property="og:description"
          content="Free menstrual cycle tracking with ovulation calculator, pregnancy probability checker, and safe days calculator."
        />
        <meta
          property="og:image"
          content="https://flowhearts.com/images/logo1.png"
        />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://flowhearts.com/" />
        <meta
          property="twitter:title"
          content="FlowHearts - Period Calculator & Women's Health Tools"
        />
        <meta
          property="twitter:description"
          content="Free menstrual cycle tracking with ovulation calculator, pregnancy probability checker, and safe days calculator."
        />
        <meta
          property="twitter:image"
          content="https://flowhearts.com/images/logo1.png"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "FlowHearts Period Calculator",
              "url": "https://flowhearts.com",
              "description": "Track menstrual cycles, calculate ovulation days, check pregnancy probability and get women's health tips",
              "applicationCategory": "HealthApplication",
              "operatingSystem": "Web",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "creator": {
                "@type": "Organization",
                "name": "FlowHearts"
              }
            }
          `}
        </script>
      </Helmet>

      {/* Hero Section */}
      <motion.section
        id="home-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative overflow-hidden py-10 px-6"
      >
        <div className="max-w-6xl mb-10 mx-auto text-center">
          <motion.h1
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            className="text-5xl font-cormorant md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600 mb-6 leading-tight"
          >
            Embrace Your <br className="md:hidden" /> Cyclical Nature
          </motion.h1>
          <p className="text-xl font-nunito md:text-2xl text-purple-700 max-w-3xl mx-auto leading-relaxed">
            Discover tools and wisdom designed for the rhythm of your body with a Menstrual cycle tracker.
          </p>
        </div>
        <QuotesSlider />
      </motion.section>

      {/* Tools Section */}
      <section
        id="tools-section"
        className="max-w-6xl font-nunito mx-auto px-6 pb-28 space-y-20"
      >
        {/* Period Tools Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-pink-300 opacity-20 blur-lg"></div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Period Tracker Card */}
            <Link
              to="/period-tracker"
              className="group block relative overflow-hidden bg-white rounded-3xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="space-y-4">
                <div className="absolute top-0 right-0 w-24 h-24 bg-pink-500/10 rounded-bl-full -mr-6 -mt-6"></div>
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-pink-100 text-pink-500">
                    <FiCalendar className="text-2xl" />
                  </div>
                  <h2 className="text-2xl font-cormorant font-bold text-purple-800">
                    Period Tracker
                  </h2>
                </div>
                <p className="text-purple-600 leading-relaxed">
                  Track your health journey with our period cycle tracker and gain better control over your body’s rhythm.
                </p>
                <CTAButton to="/period-tracker" color="pink">
                  Start Tracking
                </CTAButton>
              </div>
            </Link>

            {/* Average Day Calculator Card */}
            <Link
              to="/average-day-calculator"
              className="group block relative overflow-hidden bg-white rounded-3xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="space-y-4">
                <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 rounded-bl-full -mr-6 -mt-6"></div>
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-red-100 text-red-500">
                    <FiClock className="text-2xl" />
                  </div>
                  <h2 className="text-2xl font-cormorant font-bold text-purple-800">
                    Average Day Calculator
                  </h2>
                </div>
                <p className="text-purple-600 leading-relaxed">
                  Analyze your past menstrual cycles to calculate your average cycle length and luteal phase duration (how many days from ovulation until your next period)
                </p>
                <CTAButton to="/average-day-calculator" color="red">
                  Calculate Now
                </CTAButton>
              </div>
            </Link>
          </div>
        </motion.div>

        {/* Pregnancy Tools */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute -right-6 bottom-1/4 w-16 h-16 rounded-full bg-purple-300 opacity-20 blur-lg"></div>
          <div className="flex flex-col md:flex-row-reverse items-center gap-8">
            <div
              className="w-full md:w-1/2 h-[500px] rounded-3xl overflow-hidden relative flex items-center justify-center bg-cover bg-center"
              style={{
                backgroundImage: `url(${featureImages.pregnancyTools})`,
              }}
            >
            </div>

            <div className="w-full md:w-1/2 space-y-8">
              {/* Pregnancy Probability */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-purple-100 text-purple-500">
                    <FiHeart className="text-2xl" />
                  </div>
                  <h2 className="text-3xl font-cormorant font-bold text-purple-800">
                    Pregnancy Probability
                  </h2>
                </div>
                <p className="text-lg text-purple-600 leading-relaxed">
                 Track ovulation and symptoms to improve your chances of conception.
                </p>
                <CTAButton to="/pregnancy-probability" color="purple">
                  Check Probability
                </CTAButton>
              </div>

              {/* Due Date Estimator */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-yellow-100 text-yellow-500">
                    <FaBaby className="text-2xl" />
                  </div>
                  <h2 className="text-3xl font-cormorant font-bold text-purple-800">
                    Due Date Estimator
                  </h2>
                </div>
                <p className="text-lg text-purple-600 leading-relaxed">
                  Calculate your due date and track key baby milestones.
                </p>
                <CTAButton to="/due-date-estimator" color="yellow">
                  Estimate Due Date
                </CTAButton>
              </div>

              {/* Pregnancy Tracker */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-pink-100 text-pink-500">
                    <FaRegCalendarCheck className="text-2xl" />
                  </div>
                  <h2 className="text-3xl font-cormorant font-bold text-purple-800">
                    Pregnancy Tracker
                  </h2>
                </div>
                <p className="text-lg text-purple-600 leading-relaxed">
                Weekly pregnancy insights with symptoms, baby size, and due date tracking.
                </p>
                <CTAButton to="/pregnancy-tracker" color="pink">
                  Track Pregnancy
                </CTAButton>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Safe Days Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute -left-6 top-1/3 w-20 h-20 rounded-full bg-blue-300 opacity-20 blur-lg"></div>
          <Link to="/safe-days-calculator" className="group block">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-full md:w-1/2 h-96 rounded-3xl overflow-hidden relative">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-all duration-500 group-hover:scale-105"
                  style={{ backgroundImage: `url(${featureImages.safeDays})` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-t from-blue-500/30 to-purple-600/20"></div>
              </div>
              <div className="w-full md:w-1/2 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-blue-100 text-blue-500">
                    <FiShield className="text-2xl" />
                  </div>
                  <h2 className="text-3xl font-cormorant font-bold text-purple-800">
                    Safe Days Calculator
                  </h2>
                </div>
                <p className="text-lg text-purple-600 leading-relaxed">
                 Safe days calculator and ovulation tracking made easy.
                </p>
                <CTAButton to="/safe-days-calculator" color="blue">
                  Calculate Safe Days
                </CTAButton>
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Hormonal Exercise Guide */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute -right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-green-300 opacity-20 blur-lg"></div>
          <Link to="/hormonal-exercise" className="group block">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-full md:w-1/2 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-green-100 text-green-500">
                    <FiActivity className="text-2xl" />
                  </div>
                  <h2 className="text-3xl font-cormorant font-bold text-purple-800">
                    Hormonal Exercise
                  </h2>
                </div>
                <p className="text-lg text-purple-600 leading-relaxed">
                  Personalized workouts synced to your hormonal cycle phases.
                </p>
                <CTAButton to="/hormonal-exercise" color="green">
                  Explore Exercises
                </CTAButton>
              </div>
              <div className="w-full md:w-1/2 h-96 rounded-3xl overflow-hidden relative">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-all duration-500 group-hover:scale-105"
                  style={{
                    backgroundImage: `url(${featureImages.hormonalExercise})`,
                  }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-t from-green-500/30 to-purple-600/20"></div>
              </div>
            </div>
          </Link>
        </motion.div>
      </section>

      {/* Add Counter Section here */}
      <CounterSection />

      {/* Informational Sections */}
      <section
        id="wellness-section"
        className="bg-gradient-to-br font-nunito from-purple-100 to-pink-50 py-20"
      >
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-5xl font-cormorant font-bold text-center text-purple-800 mb-16"
          >
            Wellness Wisdom
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Wellness Tips */}
            <motion.div
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-full bg-pink-100 text-pink-500">
                  <FiMoon className="text-2xl" />
                </div>
                <h3 className="text-2xl font-cormorant font-bold text-purple-800">
                  Wellness Tips
                </h3>
              </div>
              <p className="text-purple-600 mb-6 leading-relaxed">
                Holistic hormonal wellness supporting reproductive health, mental wellbeing, and daily self-care.
              </p>
              <CTAButton to="/wellness-tips" color="pink">
                Explore Tips
              </CTAButton>
            </motion.div>

            {/* Nutrition Tips */}
            <motion.div
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-full bg-green-100 text-green-500">
                  <FaLeaf className="text-2xl" />
                </div>
                <h3 className="text-2xl font-cormorant font-bold text-purple-800">
                  Nutrition Guide
                </h3>
              </div>
              <p className="text-purple-600 mb-6 leading-relaxed">
Essential guidance on pregnancy warning signs, severe pelvic pain, and when to seek urgent care.              </p>
              <CTAButton to="/nutrition-tips" color="green">
                Discover
              </CTAButton>
            </motion.div>

            {/* Safety Awareness */}
            <motion.div
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-full bg-blue-100 text-blue-500">
                  <FaHandsHelping className="text-2xl" />
                </div>
                <h3 className="text-2xl font-cormorant font-bold text-purple-800">
                  Safety Awareness
                </h3>
              </div>
              <p className="text-purple-600 mb-6 leading-relaxed">
              Essential guidance on reproductive health safety, warning signs, and when to seek medical advice.
              </p>
              <CTAButton to="/safety-awareness" color="blue">
                Learn Safety Tips
              </CTAButton>
            </motion.div>

            {/* Pregnancy Safety */}
            <motion.div
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-full bg-red-100 text-red-500">
                  <FaClinicMedical className="text-2xl" />
                </div>
                <h3 className="text-2xl font-cormorant font-bold text-purple-800">
                  Pregnancy Safety
                </h3>
              </div>
              <p className="text-purple-600 mb-6 leading-relaxed">
              Essential safety guidelines, warning signs, and medical advice for expecting mothers.
              </p>
              <CTAButton to="/pregnancy-safety" color="red">
                Pregnancy Safety
              </CTAButton>
            </motion.div>
          </div>
        </div>
      </section>

      <motion.div
        initial={{ opacity: 0, y: 80 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <FAQSection />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 80 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <ContactSection />
      </motion.div>
      <ArticlePopup />
    </div>
  );
};

export default Home;